import React, { ComponentType } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { RouteProps } from 'react-router';
import { useOauth2Principal } from '../oauth2.hooks';
import { Loader } from '@design-system/atoms/loader';
import { StyledEngineProvider } from '@mui/material';
import AppearanceProvider from '../../theme/AppearanceProvider';

// Create a more permissive wrapper component
const withFallbackProps = <P extends object>(Component: ComponentType<P>): ComponentType<Partial<P>> => {
  return (props: Partial<P>) => {
    // Cast to any to bypass TypeScript's strict property checking
    return <Component {...(props as any)} />;
  };
};

type Props = Omit<RouteProps, 'component'> & {
  component: ComponentType<any>;
  isMui?: boolean;
};

const Oauth2ProtectedRoute = ({ component: OriginalComponent, isMui = true, ...rest }: Props) => {
  const { isAuthenticated } = useOauth2Principal();

  if (isAuthenticated == null) {
    return <Loader />;
  }

  // Wrap the component with our HOC to provide type safety
  const Component = withFallbackProps(OriginalComponent);

  return (
    <Route
      {...rest}
      render={(props) => {
        if (isAuthenticated) {
          return isMui ? (
            <StyledEngineProvider injectFirst>
              <AppearanceProvider>
                <Component {...props} />
              </AppearanceProvider>
            </StyledEngineProvider>
          ) : (
            <Component {...props} />
          );
        }
        return <Redirect to={{ pathname: '/login', state: { from: props.location } }} />;
      }}
    />
  );
};

export default Oauth2ProtectedRoute;
